<template>
  <v-dialog v-model="show" width="800">
    <v-card>
      <v-card-title class="headline">
        {{ titleText }}
      </v-card-title>

      <v-card-text v-if="currentMessage">
        <div class="mt-2 mb-2">
          <v-btn-toggle v-model="recipientType" dense mandatory>
            <v-btn dense value="groups">
              <v-icon class="pr-2" small>fas fa-user-friends</v-icon> Groups
            </v-btn>
            <v-btn dense value="locations">
              <v-icon class="pr-2" small>fas fa-map-marker-alt</v-icon>
              Locations
            </v-btn>
          </v-btn-toggle>
        </div>
        <div v-if="recipientType === 'groups'">
          <div v-if="!groups || !groups.length" class="red--text">
            You must first create a group with users
          </div>
          <v-select
            v-if="groups && groups.length"
            v-model="selectedGroups"
            :items="groups"
            item-text="name"
            item-value="groupId"
            label="Groups"
            placeholder="Select a group to send to"
            :clearable="true"
            :chips="true"
            :small-chips="true"
            :hide-details="true"
            deletable-chips
            multiple
          ></v-select>
          <div class="mb-5 user-list">
            <div
              v-if="!loading && selectedGroups && (!users || !users.length)"
              class="red--text"
            >
              You must first add users to this group
            </div>
            <div style="width: 40px;">
              <Loader v-if="loading" text="" size="small"></Loader>
            </div>
            <div v-if="selectedGroups && users && users.length">
              <v-chip
                v-for="user in users"
                :key="user.id"
                small
                class="mx-1 mt-2"
                :title="user.email"
                >{{ getUserName(user) }}</v-chip
              >
            </div>
          </div>
        </div>
        <div v-if="recipientType === 'locations'">
          <div v-if="!locations || !locations.length" class="red--text">
            You must first create a location
          </div>
          <v-select
            v-if="locations && locations.length"
            v-model="selectedLocations"
            :items="locations"
            item-text="name"
            item-value="id"
            label="Locations"
            placeholder="Select a location to send to"
            :clearable="true"
            :chips="true"
            :small-chips="true"
            :hide-details="true"
            deletable-chips
            multiple
            class="mb-5"
          ></v-select>
        </div>
        <v-text-field
          v-model="currentMessage.subject"
          label="Subject"
          placeholder="Enter the message subject"
        ></v-text-field>
        <v-textarea
          v-model="currentMessage.body"
          label="Body"
          placeholder="Enter the message text"
          rows="5"
          :rules="messageValidationRules"
          :counter="MAX_BODY_CHARACTERS"
        ></v-textarea>
        <div>
          <v-combobox
            v-model="selectedResponse"
            :items="responses"
            item-text="text"
            label="Responses"
            placeholder="Select a response (optional)"
          ></v-combobox>
          <div>
            <v-text-field
              v-if="selectedResponse && currentMessage.responses"
              v-model="currentMessage.responses[0]"
              label="Response 1"
              placeholder=" "
              class="pull-left mr-4"
            ></v-text-field>
            <v-text-field
              v-if="selectedResponse && currentMessage.responses"
              v-model="currentMessage.responses[1]"
              label="Response 2"
              placeholder=" "
              class="pull-left"
            ></v-text-field>
            <div class="clearfix"></div>
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          :disabled="processing"
          color="primary"
          text
          @click="handleClickCancel"
        >
          Cancel
        </v-btn>
        <v-btn
          :disabled="processing || !formFilled"
          @click="handleClickSaveAsDraft"
          class="ml-3"
        >
          Save as draft
        </v-btn>
        <v-btn
          :disabled="processing || !formFilled"
          @click="handleClickSend"
          class="ml-3"
        >
          Send
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import lodashOrderBy from "lodash/orderBy";
import lodashUniqBy from "lodash/uniqBy";
import lodashFind from "lodash/find";
import lodashDebounce from "lodash/debounce";
import lodashIsEqual from "lodash/isEqual";

import {
  saveMessage,
  getGroupUsers,
} from "@/services/communication/communication.service";

import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

const EMITTED_ACTIONS = {
  CLOSE: "close",
};

//timestamp to ensure state of latest getGroupUsers invocation is respected
let getGroupUsersTimestamp;

export default {
  name: "CommsMessagePopup",
  components: {
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    message: {
      type: Object,
    },
    open: {
      type: String,
    },
    action: {
      type: String,
      validator: (value) => {
        return ["add", "edit", "copy"].indexOf(value) !== -1;
      },
    },
    groups: {
      type: Array,
      required: true,
    },
    locations: {
      type: Array,
      required: true,
    },
  },
  data: function () {
    return {
      show: false,
      finished: false,
      processing: false,
      loading: false,
      failed: false,
      group: null,
      users: null,
      subject: null,
      body: null,
      selectedGroups: null,
      selectedLocations: null,
      currentMessage: {
        groups: null,
        locations: null,
        subject: null,
        body: null,
        acknowledgement: null,
      },
      MAX_BODY_CHARACTERS: 500,
      messageValidationRules: [
        (value) => {
          if (value && value.length > this.MAX_BODY_CHARACTERS) {
            return "Over Character Limit";
          }
          return true;
        },
      ],
      recipientType: "groups",
      selectedResponse: null,
      responses: [
        {
          text: "Yes/No",
          options: ["Yes", "No"],
        },
        {
          text: "Safe/Unsafe",
          options: ["Safe", "Unsafe"],
        },
        {
          text: "Arrived/Not Arrived",
          options: ["Arrived", "Not Arrived"],
        },
        {
          text: "Custom",
          options: ["", ""],
        },
      ],
    };
  },
  computed: {
    groupsSorted() {
      return lodashOrderBy(this.groups, ["name"]);
    },
    locationsSorted() {
      return lodashOrderBy(this.locations, ["name"]);
    },
    titleText() {
      return this.action === "add"
        ? "Add New Message"
        : this.action === "edit"
        ? "Edit Message"
        : "Add New Message From Copy";
    },
    formFilled() {
      let acknowledgement = true;
      if (this.currentMessage.responses) {
        if (
          !this.currentMessage.responses[0] ||
          !this.currentMessage.responses[1]
        ) {
          acknowledgement = false;
        }
      }
      return (
        this.currentMessage &&
        this.currentMessage.subject &&
        (this.currentMessage.groups || this.currentMessage.locationIds) &&
        this.currentMessage.body &&
        this.currentMessage.body.length <= this.MAX_BODY_CHARACTERS &&
        acknowledgement
      );
    },
  },
  methods: {
    getGroupUsers: lodashDebounce(async function () {
      let timeStamp = new Date().toISOString();
      getGroupUsersTimestamp = timeStamp;
      this.loading = true;
      this.users = null;
      let users = [];
      if (this.currentMessage && this.currentMessage.groups) {
        for (let i = 0; i < this.currentMessage.groups.length; i++) {
          const group = lodashFind(this.groups, [
            "groupId",
            this.currentMessage.groups[i].groupId,
          ]);
          let gUsers = await getGroupUsers({ groupId: group.groupId });
          users = users.concat(gUsers);
        }
      }
      if (timeStamp === getGroupUsersTimestamp) {
        this.users = lodashUniqBy(users, "id");
        this.loading = false;
      }
    }, 1000),
    getUserName(user) {
      if (user) {
        return user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : user.email;
      }
      return null;
    },
    openModal() {
      if (this.action === "add") {
        this.currentMessage = {
          groups: null,
          locations: null,
          subject: null,
          body: null,
          acknowledgement: null,
        };
        this.selectedResponse = null;
      }
      this.show = true;
    },
    async save(send) {
      this.processing = true;

      this.currentMessage.isDraft = !send;
      const grps = [];
      if (this.currentMessage.groups) {
        this.currentMessage.groups.forEach((grp) => {
          const group = lodashFind(this.groups, ["groupId", grp.groupId]);
          if (group) {
            grps.push({ groupId: group.groupId, groupName: group.name });
          }
        });
      }
      this.currentMessage.groups = grps;

      let params = {
        message: this.currentMessage,
      };

      try {
        await saveMessage(params);
      } catch (error) {
        console.error(error);
        this.failed = true;
      } finally {
        this.processing = false;
        this.finished = true;
        this.show = false;
      }
    },
    async handleClickSaveAsDraft() {
      await this.save(false);
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.mass_communication,
        EVENT_ACTIONS.clicked,
        "Edit Mode - Save a Draft"
      );
    },
    async handleClickSend() {
      await this.save(true);
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.mass_communication,
        EVENT_ACTIONS.clicked,
        "Edit Mode - Send a Message"
      );
    },
    handleClickCancel: function () {
      this.currentMessage = {
        groups: null,
        locations: null,
        subject: null,
        body: null,
        acknowledgement: null,
      };
      this.selectedResponse = null;
      this.show = false;
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.mass_communication,
        EVENT_ACTIONS.clicked,
        "Edit Mode - Cancel a Message"
      );
    },
  },
  watch: {
    open() {
      this.show = !!this.open;
    },
    show() {
      if (!this.show) {
        this.$emit(EMITTED_ACTIONS.CLOSE);
      }
      this.selectedGroups = null;
      this.selectedLocations = null;
      this.users = null;
      this.currentMessage = {
        messageId: null,
        groups: null,
        locations: null,
        subject: null,
        body: null,
        acknowledgement: null,
      };
      this.selectedResponse = null;
    },
    message() {
      this.loading = true; //so the user list doesn't show error on load
      this.currentMessage = this.message
        ? this.message
        : {
            messageId: null,
            groups: null,
            locations: null,
            subject: null,
            body: null,
            acknowledgement: null,
          };
      this.selectedGroups = this.message.groups ? this.message.groups : null;
      this.selectedLocations = this.message.locationIds
        ? this.message.locationIds
        : null;
      if (this.selectedLocations && this.selectedLocations.length > 0) {
        this.recipientType = "locations";
      }
      this.selectedResponse = null;
      if (this.currentMessage.responses) {
        let defaultResponse = this.responses.find((x) =>
          lodashIsEqual(x.options, this.currentMessage.responses)
        );
        if (defaultResponse) {
          this.selectedResponse = defaultResponse;
        } else {
          this.responses[
            this.responses.findIndex((x) => x.text == "Custom")
          ].options = [this.message.responses[0], this.message.responses[1]];
          this.selectedResponse = this.responses.find(
            (x) => x.text == "Custom"
          );
        }
      }
    },
    selectedGroups: {
      async handler() {
        if (this.selectedGroups) {
          const groups = this.selectedGroups.map((group) => {
            return group.groupId ? group : { groupId: group };
          });
          this.currentMessage.groups = groups;
          await this.getGroupUsers();
        }
      },
    },
    selectedLocations: {
      async handler() {
        if (this.selectedLocations) {
          this.currentMessage.locationIds = this.selectedLocations;
          await this.getGroupUsers();
        }
      },
    },
    selectedResponse: {
      handler() {
        if (this.selectedResponse) {
          this.currentMessage.responses = [
            this.selectedResponse.options[0].trim(),
            this.selectedResponse.options[1].trim(),
          ];
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.user-list {
  overflow-y: auto;
  max-height: 130px;
}
</style>
